import createCommonModule from '@/common/store/modules/common';
import {
  LOAD_BALANCER_POOL_IN_PROGRESS_STATUSES,
  LOAD_BALANCER_POOL_MEMBER_STATUSES,
  LOAD_BALANCER_POOL_STATUSES,
  POOL_BALANCING_ALGORITHMS,
  POOL_MEMBER_STATUS_ICONS,
  POOL_STATUS_ICONS
} from '@/common/constants/loadBalancers.constants';
import i18n from '@/common/i18n';

function loadBalancerPool(attributes) {
  const {
    backend_protocol: backendProtocol,
    backend_protocol_port: backendProtocolPort,
    connection_limit: connectionLimit,
    description,
    enabled: isEnabled,
    healthmonitor: healthMonitor,
    lb_algorithm: lbAlgorithm,
    loadbalancer_id: loadBalancerId,
    id,
    members,
    name,
    project_id: projectId,
    protocol,
    protocol_port: protocolPort,
    sticky_session: stickySession,
    status,
    updated_at: updatedAt,
    insert_headers: insertHeaders,
    tls_enabled: tlsEnabled,
    proxy_protocol_enabled: proxyProtocolEnabled,
    allowed_cidrs
  } = attributes;

  return {
    backendProtocol,
    backendProtocolPort,
    connectionLimit,
    description,
    healthMonitor,
    id,
    isEnabled,
    lbAlgorithm,
    loadBalancerId,
    members,
    name,
    projectId,
    protocol,
    protocolPort,
    stickySession,
    status,
    updatedAt,
    insertHeaders,
    tlsEnabled,
    proxyProtocolEnabled,
    allowed_cidrs
  };
}

export default createCommonModule({
  name: 'pools',
  resource: '/api/v2/compute/lbaas/pools',
  Model: loadBalancerPool,
  plural: ({ data }) => data,
  socketUpdates: true,
  getters: {
    statusText: () => ({
      [LOAD_BALANCER_POOL_STATUSES.CREATING]: i18n.t('Creating...'),
      [LOAD_BALANCER_POOL_STATUSES.ACTIVE]: i18n.t('Active'),
      [LOAD_BALANCER_POOL_STATUSES.CONFIGURING]: i18n.t('Configuring...'),
      [LOAD_BALANCER_POOL_STATUSES.DELETING]: i18n.t('Deleting...'),
      [LOAD_BALANCER_POOL_STATUSES.ERROR]: i18n.t('Error'),
    }),
    membersStatusText: () => ({
      [LOAD_BALANCER_POOL_MEMBER_STATUSES.HEALTHY]: i18n.t('Healthy'),
      [LOAD_BALANCER_POOL_MEMBER_STATUSES.UNHEALTHY]: i18n.t('Unhealthy'),
      [LOAD_BALANCER_POOL_MEMBER_STATUSES.DISABLED]: i18n.t('Disabled'),
      [LOAD_BALANCER_POOL_MEMBER_STATUSES.UNKNOWN]: i18n.t('Unknown'),
      [LOAD_BALANCER_POOL_STATUSES.ERROR]: i18n.t('Error'),
    }),
    poolAlgorithms: () => ({
      [POOL_BALANCING_ALGORITHMS.LEAST_CONNECTIONS]: 'Least connections',
      [POOL_BALANCING_ALGORITHMS.ROUND_ROBIN]: 'Round robin',
      [POOL_BALANCING_ALGORITHMS.SOURCE_IP]: 'Source IP',
    }),
    _mapItem: (state, getters, rootState, rootGetters) => (id) => {
      const pool = state.itemsMap[id];
      if (!pool) {
        return null;
      }
      const project = rootGetters['projects/getItemById']?.(pool.projectId);
      const membersStat = {
        healthy: pool.members.filter(m => m.status === LOAD_BALANCER_POOL_MEMBER_STATUSES.HEALTHY).length,
        unhealthy: pool.members.filter(m => m.status === LOAD_BALANCER_POOL_MEMBER_STATUSES.UNHEALTHY).length,
        error: pool.members.filter(m => m.status === LOAD_BALANCER_POOL_MEMBER_STATUSES.ERROR).length,
        disabled: pool.members.filter(m => m.status === LOAD_BALANCER_POOL_MEMBER_STATUSES.DISABLED).length,
        unknown: pool.members.filter(m => m.status === LOAD_BALANCER_POOL_MEMBER_STATUSES.UNKNOWN).length
      };

      const getStatus = member => member.enabled ? member.status : LOAD_BALANCER_POOL_MEMBER_STATUSES.DISABLED;
      return {
        ...pool,
        members: pool.members.map((member) => {
          const statusMember = getStatus(member);
          return {
            ...member,
            statusIcon: POOL_MEMBER_STATUS_ICONS[statusMember],
            statusText: getters.membersStatusText[statusMember] || statusMember
          };
        }),
        statusText: getters.statusText[pool.status] || pool.status,
        statusIcon: POOL_STATUS_ICONS[pool.status] || POOL_STATUS_ICONS.ERROR,
        lbAlgorithmText: getters.poolAlgorithms[pool.lbAlgorithm] || pool.lbAlgorithm,
        membersStat,
        totalMembers: pool.members.length,
        projectName: project?.name,
        isErrored: pool.status === LOAD_BALANCER_POOL_STATUSES.ERROR,
        isInProgress: LOAD_BALANCER_POOL_IN_PROGRESS_STATUSES.indexOf(pool.status) > -1,
      };
    },
  },
  actions: {
    UPDATE_ALLOWED_CIDRS({ dispatch }, { id, body }) {
      // TODO: jbadza: Workaround until VSTOR-85125 is fixed.
      // We need to update the pool and then fetch it again to get the Configuring state.
      return dispatch('UPDATE', { id, body })
        .then(() => dispatch('FETCH_ITEM', { id }));
    },
  },
});
